import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { AlertCircle, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import type {
  ShopOverrideRuleActionDto,
  ShopOverrideRuleDto,
} from "@babackapp/private-api-client/types";
import { FieldIdDefinition, getActionTypeInfo } from "@/lib/override-rules";
import { OverrideRuleDialog } from "./override-rule-dialog";
import { Switch } from "@babackapp/ui/components/switch";
import {
  OverrideRulesProvider,
  useOverrideRules,
} from "./override-rules-context";
import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { RuleSortableItem } from "@/components/override-rules/rule-sortable-item.tsx";
import useTranslation from "@babackapp/translations/use-translation";

interface OverrideRulesManagerProps {
  actionType: ShopOverrideRuleActionDto["type"];
  actionValueOptions?: { label: string; key: string }[];
  title?: string;
  description?: string;
  className?: string;
  excludeFields?: FieldIdDefinition[];
}

export function OverrideRulesManager({
  actionType,
  actionValueOptions,
  title,
  description,
  excludeFields,
  className = "",
}: OverrideRulesManagerProps) {
  return (
    <OverrideRulesProvider>
      <OverrideRulesManagerContent
        actionType={actionType}
        actionValueOptions={actionValueOptions}
        title={title}
        description={description}
        className={className}
        excludeFields={excludeFields}
      />
    </OverrideRulesProvider>
  );
}

export function OverrideRulesManagerContent({
  actionType,
  actionValueOptions,
  title,
  description,
  excludeFields,
  className = "",
}: OverrideRulesManagerProps) {
  const t = useTranslation();
  const {
    rules,
    isLoading,
    error,
    deleteRule,
    deleteRulePending,
    updateRules,
    updateRulesPending,
  } = useOverrideRules();

  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    existingRule?: ShopOverrideRuleDto;
  }>({
    isOpen: false,
  });

  const [deleteState, setDeleteState] = useState<{
    isOpen: boolean;
    ruleId?: string;
    ruleName?: string;
  }>({
    isOpen: false,
  });

  // Filter rules by action type
  const filteredRules = useMemo(() => {
    return [...rules] // 👈 crée une copie
      .filter((rule) => rule.action.type === actionType)
      .sort((a, b) => a.priority - b.priority);
  }, [rules, actionType]);

  const actionInfo = getActionTypeInfo(actionType);

  const [localRules, setLocalRules] = useState(filteredRules);

  useEffect(() => {
    setLocalRules(filteredRules);
  }, [filteredRules]);

  // Open dialog for creating new rule
  const handleCreateRule = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setDialogState({
      isOpen: true,
    });
  };

  // Open dialog for editing existing rule
  const handleEditRule = (rule: ShopOverrideRuleDto) => {
    setDialogState({
      isOpen: true,
      existingRule: rule,
    });
  };

  // Open delete confirmation
  const handleDeleteRule = (rule: ShopOverrideRuleDto) => {
    setDeleteState({
      isOpen: true,
      ruleId: rule.id,
      ruleName: rule.name,
    });
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    if (deleteState.ruleId) {
      deleteRule(deleteState.ruleId);
    }
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogState({ isOpen: false });
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteState({ isOpen: false });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localRules.findIndex((r) => r.id === active.id);
    const newIndex = localRules.findIndex((r) => r.id === over.id);
    const reordered = arrayMove(localRules, oldIndex, newIndex);

    const updated = reordered.map((r, i) => ({
      ...r,
      priority: i + 1,
    }));

    setLocalRules(updated);

    updateRules(
      updated.map((r) => ({
        id: r.id,
        name: r.name,
        description: r.description,
        priority: r.priority,
        isActive: r.isActive,
        action: r.action,
        conditions: r.conditions,
      })),
      {
        onSuccess: () => {},
      },
    );
  };

  const switchRuleActive = (rule: ShopOverrideRuleDto) => {
    const updatedRule = {
      ...rule,
      isActive: !rule.isActive,
    };
    updateRules([
      {
        id: updatedRule.id,
        name: updatedRule.name,
        description: updatedRule.description,
        priority: updatedRule.priority,
        isActive: updatedRule.isActive,
        action: updatedRule.action,
        conditions: updatedRule.conditions,
      },
    ]);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">
                {title || actionInfo?.label || actionType}
              </CardTitle>
              {description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {description}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            {t("overrideRules.misc.loadingRules")}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base text-red-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {t("overrideRules.misc.errorLoadingRules")}
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-600">
            {(error as Error)?.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <OverrideRulesProvider>
      <div className={className}>
        <div className="gap-2">
          {localRules.length > 0 && (
            <>
              <div className="text-xs font-medium text-muted-foreground mb-1 mt-2">
                {t("overrideRules.misc.exceptionRules")}
              </div>
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={localRules.map((rule) => rule.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {localRules.map((rule) => (
                    <RuleSortableItem key={rule.id} id={rule.id}>
                      <div
                        className="group flex items-cente text-xs hover:bg-gray-200 rounded gap-4 p-1 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleEditRule(rule);
                        }}
                      >
                        <span
                          className="text-sm self-center font-semibold min-w-[1.2em] text-end"
                          title={t("overrideRules.misc.priority")}
                        >
                          {rule.priority}
                        </span>
                        <div className="flex gap-1 flex-1 items-center">
                          <span className="font-medium">{rule.name}</span>
                          <span className="text-muted-foreground">
                            {rule.description}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteRule(rule);
                          }}
                          className="h-4 w-4 p-0 text-red-600 hover:text-red-800 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={deleteRulePending}
                        >
                          <Trash2 className="h-2 w-2" />
                        </Button>
                        <Switch
                          title={
                            rule.isActive
                              ? t("overrideRules.misc.active")
                              : t("overrideRules.misc.notActive")
                          }
                          checked={rule.isActive}
                          onClick={(e) => {
                            e.stopPropagation();
                            switchRuleActive(rule);
                          }}
                          disabled={updateRulesPending}
                        />
                      </div>
                    </RuleSortableItem>
                  ))}
                </SortableContext>
              </DndContext>
            </>
          )}

          <Button
            onClick={(e) => handleCreateRule(e)}
            variant="link"
            size="sm"
            className="text-xs text-muted-foreground hover:text-foreground p-0"
          >
            <Plus className="h-3 w-3 mr-0" />
            {t("overrideRules.misc.addRule")}
          </Button>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <OverrideRuleDialog
        isOpen={dialogState.isOpen}
        onOpenChange={handleCloseDialog}
        existingRule={dialogState.existingRule}
        actionType={actionType}
        actionValueOptions={actionValueOptions}
        excludeFields={excludeFields}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteState.isOpen}
        onOpenChange={handleCloseDeleteDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("overrideRules.misc.deleteRule")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("overrideRules.misc.confirmDeleteRule", {
                ruleName: deleteState.ruleName,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRulePending}>
              {t("misc.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteRulePending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteRulePending ? t("misc.deleting") : t("misc.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </OverrideRulesProvider>
  );
}
